import { useCallback, useEffect, useMemo, useState } from "react";
import i18n from "@/i18n";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { License } from "@/pages/licenses/hooks/useMyLicenses";

export type LicenseEventType = "issued" | "renewed" | "activated" | "released";

export interface LicenseEvent {
  id: string;
  type: LicenseEventType;
  at: string;
  licenseId: number | null;
  productName: string;
  edition: string | null;
  detail: string | null;
}

interface DeviceRow {
  id: number;
  license_id: number | null;
  device_name: string | null;
  machine_code: string | null;
  activated_at: string | null;
  released_at: string | null;
}

interface RenewalAppRow {
  id: number;
  renew_license_id: number | null;
  status: string;
  reviewed_at: string | null;
  created_at: string;
  product_name: string;
}

interface UseLicenseTimelineResult {
  events: LicenseEvent[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

// 客户侧许可证变更时间线：把「签发 / 续期 / 设备激活 / 设备解绑」统一回放。
// 数据来自本人名下的许可证 + 设备（RLS 仅本人可读）+ 已通过的续期申请，纯前端聚合。
export function useLicenseTimeline(licenses: License[]): UseLicenseTimelineResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [devices, setDevices] = useState<DeviceRow[]>([]);
  const [renewals, setRenewals] = useState<RenewalAppRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setDevices([]);
      setRenewals([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [deviceRes, renewalRes] = await Promise.all([
        supabase
          .from("license_devices")
          .select("id, license_id, device_name, machine_code, activated_at, released_at")
          .eq("user_id", user.id),
        supabase
          .from("license_applications")
          .select("id, renew_license_id, status, reviewed_at, created_at, product_name")
          .eq("user_id", user.id)
          .eq("status", "approved")
          .not("renew_license_id", "is", null),
      ]);
      if (deviceRes.error) throw deviceRes.error;
      if (renewalRes.error) throw renewalRes.error;
      setDevices((deviceRes.data as DeviceRow[]) ?? []);
      setRenewals((renewalRes.data as RenewalAppRow[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : i18n.t("timeline.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const events = useMemo(() => {
    const byId = new Map(licenses.map((license) => [license.id, license]));
    const list: LicenseEvent[] = [];

    licenses.forEach((license) => {
      if (!license.issued_at) return;
      list.push({
        id: `issued-${license.id}`,
        type: "issued",
        at: license.issued_at,
        licenseId: license.id,
        productName: license.product_name,
        edition: license.edition,
        detail: null,
      });
    });

    renewals.forEach((app) => {
      if (app.renew_license_id == null) return;
      const license = byId.get(app.renew_license_id);
      list.push({
        id: `renewed-${app.id}`,
        type: "renewed",
        at: app.reviewed_at ?? app.created_at,
        licenseId: app.renew_license_id,
        productName: license?.product_name ?? app.product_name,
        edition: license?.edition ?? null,
        detail: null,
      });
    });

    devices.forEach((device) => {
      const license = device.license_id != null ? byId.get(device.license_id) : undefined;
      const base = {
        licenseId: device.license_id,
        productName: license?.product_name ?? "",
        edition: license?.edition ?? null,
        detail: device.device_name || device.machine_code || null,
      };
      if (device.activated_at) {
        list.push({ id: `act-${device.id}`, type: "activated", at: device.activated_at, ...base });
      }
      if (device.released_at) {
        list.push({ id: `rel-${device.id}`, type: "released", at: device.released_at, ...base });
      }
    });

    return list.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
  }, [licenses, devices, renewals]);

  return { events, loading: loading || authLoading, error, reload: load };
}